import { useQuery } from "@apollo/client";
import { useLocalStorage } from "@aurem/utils";
import type { ReactNode } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

import { useLocation } from "react-router-dom";
import { GetEmploymentQuery, gql } from "types";

const GET_EMPLOYMENT = gql(/* GraphQL */ `
  query GetEmployment($employmentId: ID!) {
    user {
      id
      citizenships
      findEmployment(employmentId: $employmentId) {
        id
        firstName
        lastName
        endDate
        productAssignments {
          id
          employeeId
          endDate
          productInstance {
            id
            name
            product {
              id
            }
            company {
              id
            }
          }
        }
        company {
          id
          name
        }
      }
    }
  }
`);

type Employment = GetEmploymentQuery["user"]["findEmployment"];

interface EmploymentContextType {
  employmentId: string | null;
  setEmploymentId: (id: string | null) => void;
  employment: Employment | null;
  isTerminated: boolean;
  loading: boolean;
  refetch: () => void;
}
const EmploymentContext = createContext<EmploymentContextType | undefined>(
  undefined,
);

export const useEmployment = (): EmploymentContextType => {
  const context = useContext(EmploymentContext);
  if (!context) {
    throw new Error("useEmployment must be used within EmploymentProvider");
  }
  return context;
};

export const EmploymentProvider = ({ children }: { children: ReactNode }) => {
  const [latestEmployment, setLatestEmployment] = useLocalStorage(
    "employmentId",
    null,
  );

  const location = useLocation();
  const match = location.pathname.match(/\/employment\/([^/]+)/);
  const urlEmploymentId = match?.[1] ?? latestEmployment;

  const [manualEmploymentId, setManualEmploymentId] = useState<string | null>(
    null,
  );
  const effectiveEmploymentId = manualEmploymentId ?? urlEmploymentId ?? null;
  const { data, loading, error, refetch } = useQuery(GET_EMPLOYMENT, {
    variables: { employmentId: effectiveEmploymentId as string },
    skip: !effectiveEmploymentId,
    fetchPolicy: "cache-and-network",
  });

  const employment = data?.user?.findEmployment ?? null;
  const isTerminated =
    employment?.endDate != null && new Date(employment.endDate) < new Date();

  useEffect(() => {
    if (loading || error) return;
    if (employment?.id && latestEmployment !== employment.id) {
      setLatestEmployment(employment.id);
    }
    if (!employment && effectiveEmploymentId && latestEmployment) {
      setLatestEmployment(null);
      setManualEmploymentId(null);
    }
  }, [
    loading,
    error,
    employment,
    effectiveEmploymentId,
    latestEmployment,
    setLatestEmployment,
  ]);

  const resolvedEmploymentId =
    employment?.id ?? (loading ? effectiveEmploymentId : null);

  const setEmploymentId = useCallback(
    (id: string | null) => {
      setManualEmploymentId(id);
      if (id) {
        setLatestEmployment(id);
      } else {
        setLatestEmployment(null);
      }
    },
    [setLatestEmployment],
  );

  return (
    <EmploymentContext.Provider
      value={{
        employmentId: resolvedEmploymentId,
        setEmploymentId,
        employment,
        isTerminated,
        loading,
        refetch,
      }}
    >
      {children}
    </EmploymentContext.Provider>
  );
};
